import React, { FC, useEffect } from 'react'
import { Box } from '@moonpig/launchpad-components'
import { SkeletonCarousel, Carousel } from '@moonpig/web-shared-components'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { useCoreLocaleText } from '@moonpig/web-core-locale-text'
import type { ProductInfoProduct } from '../../types'
import { useMoreLikeThisProductSelect } from '../../modules/MoreLikeThis/hooks/useMoreLikeThisProductSelect'
import {
  getProductDisplayImageUrl,
  hasProductDisplayImage,
} from '../../utils/getProductDisplayImageUrl'
import { CarouselWrapper } from './CarouselWrapper'
import { StyledHeading } from './Heading'
import { CarouselImage } from './CarouselImage'
import { CarouselItem } from './CarouselItem'
import { useInView } from 'react-intersection-observer'
import { useTrackGAEventOnce } from '@moonpig/web-core-analytics'
import { createViewItemListGAEvent } from '../../analytics'

type RecommendationCarouselProps = {
  title: string
  titleId: string
  products: ProductInfoProduct[]
  loading: boolean
  listName: string
  listPosition: string
}

const CarouselShell: FC<{
  title: string
  titleId: string
  loading: boolean
  children: React.ReactNode
  products: ProductInfoProduct[]
  listPosition: string
}> = ({ title, titleId, loading, products, children, listPosition }) => {
  const { trackGAEventOnce } = useTrackGAEventOnce()
  const [carouselRef, inView] = useInView({ triggerOnce: true })

  useEffect(() => {
    if (!inView || loading || products.length === 0) {
      return
    }
    trackGAEventOnce(
      createViewItemListGAEvent({
        label: `product details | ${title} | carousel | ${listPosition}`,
        products,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, loading, products.length])

  if (loading) {
    return (
      <SkeletonCarousel
        numberOfBoxes={6}
        pl={{ xs: 5, md: 6 }}
        pr={{ xs: 0, md: 6 }}
      />
    )
  }

  if (products.length === 0) {
    return null
  }

  return (
    <CarouselWrapper ref={carouselRef}>
      <StyledHeading id={titleId} level="h3">
        {title}
      </StyledHeading>
      <Box px={{ md: 10 }}>
        <Carousel ariaLabel={title}>{children}</Carousel>
      </Box>
    </CarouselWrapper>
  )
}

export const RecommendationCarousel: FC<RecommendationCarouselProps> = ({
  title,
  titleId,
  products,
  loading,
  listName,
  listPosition,
}) => {
  const commonLocaliseText = useCoreLocaleText()
  const isDynamicImagesEnabled = useExperiment('search-use-dynamic-images', {
    fallback: false,
  })
  const productsWithImages = products.filter(product =>
    hasProductDisplayImage(product, isDynamicImagesEnabled),
  )
  const handleProductClick = useMoreLikeThisProductSelect({
    products: productsWithImages,
    listName,
    listPosition,
    component: 'carousel',
  })

  return (
    <CarouselShell
      title={title}
      titleId={titleId}
      loading={loading}
      products={productsWithImages}
      listPosition={listPosition}
    >
      {productsWithImages.map((product, index) => {
        const { id, title: productTitle } = product
        const imageUrl = getProductDisplayImageUrl(
          product,
          isDynamicImagesEnabled,
        )

        return (
          <CarouselItem key={id}>
            <button
              type="button"
              onClick={() => handleProductClick(index)}
              aria-label={commonLocaliseText('common.button_to_select_title', {
                title: productTitle,
              })}
            >
              <CarouselImage title={productTitle} mainImageUrl={imageUrl} />
            </button>
          </CarouselItem>
        )
      })}
    </CarouselShell>
  )
}
